/**
 * German strings for text that originates outside src/client: src/shared
 * catalogues (cars, modes, presets, crew modules, challenges, wagers,
 * signatures, style actions), server/RoomError messages and map JSON.
 * Keys are the exact English runtime text.
 */
export const DE_SHARED: Readonly<Record<string, string>> = {
  // ── cars.ts: className ────────────────────────────────────────────────
  'Street / Balanced': 'Street / Allrounder',
  'Exotic / Velocity': 'Exot / Topspeed',
  'Roadster / Sprint': 'Roadster / Sprint',
  'Track / Precision': 'Rennstrecke / Präzision',
  'Muscle / Power': 'Muscle / Kraft',
  'Heavy / Off-road': 'Schwer / Offroad',
  'Heavy / Big rig': 'Schwer / Sattelzug',
  'Tracked / Combat': 'Ketten / Kampf',
  'Superbike / Velocity': 'Superbike / Topspeed',
  'Scrambler / All-terrain': 'Scrambler / Gelände',
  'Novelty / Snack Attack': 'Kurios / Snack-Attacke',
  'Novelty / Bath Time': 'Kurios / Badezeit',
  'Novelty / Living Room': 'Kurios / Wohnzimmer',
  'Novelty / Monster Micro': 'Kurios / Monster-Mini',

  // ── cars.ts: description ──────────────────────────────────────────────
  'Predictable grip and a broad power band. The perfect all-rounder.':
    'Berechenbarer Grip und ein breites Drehzahlband. Der perfekte Allrounder.',
  'Low, planted and brutally fast once the road opens up.':
    'Flach, satt auf der Straße und brutal schnell, sobald es geradeaus geht.',
  'Lightweight response and explosive acceleration through technical sectors.':
    'Leichtfüßig, direkt und explosiv beim Beschleunigen in technischen Sektoren.',
  'Aero-led precision for drivers who refuse to lift.':
    'Aerodynamische Präzision für alle, die nie vom Gas gehen.',
  'Heavyweight torque, long slides and an unmistakable silhouette.':
    'Schwergewichtiges Drehmoment, lange Slides und eine unverwechselbare Silhouette.',
  'Six driven wheels, huge suspension travel and the confidence to take the rough line.':
    'Sechs angetriebene Räder, riesige Federwege und genug Selbstvertrauen für die raue Linie.',
  'A stripped racing hauler with towering torque and enough mass to own every lane.':
    'Ein entkernter Renn-Truck mit gewaltigem Drehmoment und genug Masse, um jede Spur zu beherrschen.',
  'A working battle tank: rotate the turret with Q/E and fire its cannon with F.':
    'Ein echter Kampfpanzer: Dreh den Turm mit Q/E und feuere die Kanone mit F.',
  'A featherweight superbike with explosive drive, precise braking and a committed racing lean.':
    'Ein federleichtes Superbike mit explosivem Antritt, präzisen Bremsen und kompromissloser Schräglage.',
  'Long-travel suspension and knobbly tyres make sand and rough shortcuts part of the racing line.':
    'Langhubige Federung und Stollenreifen machen Sand und raue Abkürzungen zur Ideallinie.',
  'A grilled frank in a toasted bun with V8 relish. Heavy on the mustard, heavier on the slides.':
    'Ein gegrilltes Würstchen im getoasteten Brötchen mit V8-Relish. Viel Senf, noch mehr Slides.',
  'A featherweight rubber duck with a big smile, a bobbing head and surprising bite through the bends.':
    'Eine federleichte Quietscheente mit breitem Grinsen, wippendem Kopf und erstaunlich viel Biss in den Kurven.',
  'A tufted velvet Chesterfield on a go-kart frame. Table lamps for headlights, popcorn included.':
    'Ein Chesterfield-Samtsofa auf einem Kart-Rahmen. Tischlampen als Scheinwerfer, Popcorn inklusive.',
  'A two-tone bubble car perched on monster-truck tyres and long-travel coilovers. Kerbs are optional.':
    'Ein zweifarbiger Kabinenroller auf Monstertruck-Reifen und langhubigen Gewindefahrwerken. Randsteine sind optional.',

  // ── arcade-presets.ts ─────────────────────────────────────────────────
  'STREET RULES': 'STREET-REGELN',
  'CAREER ELIGIBLE': 'ZÄHLT FÜR KARRIERE',
  'The authored NIGHTSHIFT handling, traffic and reward rules.':
    'Das originale NIGHTSHIFT-Handling mit Verkehr und Belohnungen wie vorgesehen.',
  'BOOST NEVER SLEEPS': 'BOOST SCHLÄFT NIE',
  'Nitro refills rapidly so every straight becomes an attack window.':
    'Nitro lädt rasend schnell nach – jede Gerade wird zur Angriffschance.',
  'BIGGER SHUNTS': 'HÄRTERE CRASHS',
  'Stronger collision impulses with reduced damage for chaotic pack racing.':
    'Heftigere Kollisionen bei weniger Schaden – für chaotische Rudelrennen.',
  'TRAFFIC PANIC': 'VERKEHRSPANIK',
  'DENSE CITY FLOW': 'DICHTER STADTVERKEHR',
  'A denser deterministic traffic stream turns overtaking into a moving puzzle.':
    'Dichterer Verkehr macht jedes Überholmanöver zum rollenden Puzzle.',
  'LOW GRAVITY': 'GERINGE SCHWERKRAFT',
  'LONGER AIRTIME': 'MEHR AIRTIME',
  'Reduced gravity makes ramps and mid-air control the centre of the event.':
    'Weniger Schwerkraft macht Rampen und Kontrolle in der Luft zum Herzstück des Events.',

  // ── crew-modules.ts ───────────────────────────────────────────────────
  'STREET SETUP': 'STREET-SETUP',
  'The original factory-balanced arcade setup.': 'Das originale, ab Werk ausbalancierte Arcade-Setup.',
  'NO TRADE-OFF': 'KEIN KOMPROMISS',
  'Quicker response and stronger braking for technical roads.':
    'Direkteres Ansprechen und stärkere Bremsen für technische Strecken.',
  '+10% HANDLING · +4% BRAKING · -3% TOP SPEED': '+10% HANDLING · +4% BREMSEN · -3% TOPSPEED',
  'More straight-line urgency and richer nitro rewards.':
    'Mehr Biss auf der Geraden und fettere Nitro-Belohnungen.',
  '+4% ACCEL · -6% HANDLING': '+4% BESCHL. · -6% HANDLING',
  'Reinforced bodywork keeps a bad contact from ending the run.':
    'Verstärkte Karosserie – ein harter Kontakt beendet nicht gleich dein Rennen.',
  '-24% DAMAGE · +9% MASS · -3% ACCEL': '-24% SCHADEN · +9% MASSE · -3% BESCHL.',

  // ── live-challenges.ts ────────────────────────────────────────────────
  'CLEAN SECTOR': 'SAUBERER SEKTOR',
  'Reach the next sector without contact.': 'Erreiche den nächsten Sektor ohne Kontakt.',
  'SECTOR SECURED': 'SEKTOR GESICHERT',
  'HUNT THE PACK': 'JAG DAS FELD',
  'Complete one clean overtake.': 'Schaffe ein sauberes Überholmanöver.',
  'TARGET PASSED': 'ZIEL ÜBERHOLT',
  'BREAK THE LINE': 'VERLASS DIE LINIE',
  'Complete an alternate route.': 'Fahr eine Alternativroute.',
  'ROUTE CLEARED': 'ROUTE GESCHAFFT',
  'SPEED TRAP': 'BLITZER',
  'Hit the target speed before time expires.': 'Knack das Zieltempo, bevor die Zeit abläuft.',
  'TRAP BEATEN': 'BLITZER GEKNACKT',
  'KEEP IT ALIVE': 'KETTE HALTEN',
  'Build the requested style chain.': 'Bau die geforderte Style-Kette auf.',
  'CHAIN LOCKED': 'KETTE GESICHERT',

  // ── gameplay-fun.ts: wagers ───────────────────────────────────────────
  'CLEAN ESCAPE': 'SAUBERE FLUCHT',
  'Finish without a damaging crash or manual recovery.':
    'Komm ins Ziel ohne schweren Crash oder manuelles Zurücksetzen.',
  'SURGICAL PASSES': 'PRÄZISE MANÖVER',
  'Complete three clean overtakes before the flag.':
    'Schaffe drei saubere Überholmanöver vor der Zielflagge.',
  'NO SAFE LINE': 'KEINE SICHERE LINIE',
  'Complete one authored extreme alternate route.': 'Fahr eine der extremen Alternativrouten.',

  // ── gameplay-fun.ts: city set pieces ──────────────────────────────────
  'BERN TRAM CROSSING': 'TRAMKREUZUNG BERN',
  'TRAM LINE LIVE': 'TRAM IM ANMARSCH',
  'CHAPEL BRIDGE SERVICE': 'WARTUNG KAPELLBRÜCKE',
  'BRIDGE DECK MOVING': 'BRÜCKE IN BEWEGUNG',
  'MESSE NIGHT WORKS': 'NACHTBAUSTELLE MESSE',
  'CREW ON THE CIRCUIT': 'ARBEITER AUF DER STRECKE',
  'CAPITAL CONVOY': 'HAUPTSTADT-KONVOI',
  'ESCORT CROSSING': 'ESKORTE KREUZT',

  // ── race-events.ts: eventModeLabel ('STYLE RUSH', 'HEAT RUN', 'ROAD RAGE' stay) ──
  'STREET SPRINT': 'STRASSENSPRINT',
  'TIME TRIAL': 'ZEITFAHREN',
  'ELIMINATION': 'ELIMINIERUNG',
  'CIRCUIT RACE': 'RUNDSTRECKE',

  // ── race-events.ts: eventResultLabel ('DNF' stays) ────────────────────
  'WIN': 'SIEG',
  'OUT': 'RAUS',
  'SCORED': 'GEWERTET',
  'FINISHED': 'IM ZIEL',
  'BUST': 'GESCHNAPPT',

  // ── types.ts: StyleAction callouts ────────────────────────────────────
  'GOOD LAUNCH': 'GUTER START',
  'PERFECT LAUNCH': 'PERFEKTER START',
  'WHEELSPIN': 'DURCHDREHER',
  'DRAFT': 'WINDSCHATTEN',
  'SLINGSHOT': 'KATAPULT',
  'AIR': 'SPRUNG',
  'NEAR MISS': 'KNAPP VORBEI',
  'OVERTAKE': 'ÜBERHOLT',
  'CLEAN PASS': 'SAUBER VORBEI',
  'REVENGE': 'REVANCHE',
  'RISK LINE': 'RISIKOLINIE',
  'SHORTCUT': 'ABKÜRZUNG',
  'LOOP MASTER': 'LOOPING-MEISTER',
  'GOOD CORNER': 'GUTE KURVE',
  'PERFECT CORNER': 'PERFEKTE KURVE',
  'BOOST ZONE': 'BOOST-ZONE',
  'PIT CREW': 'BOXENCREW',
  'CROWD LINE': 'PUBLIKUMSLINIE',
  'LIVE CONTRACT': 'LIVE-AUFTRAG',
  'HEAT UP': 'HEAT STEIGT',
  'BANKED': 'GESICHERT',
  'SIGNATURE': 'SIGNATUR',
  'REWIND': 'ZURÜCKGESPULT',
  'BOSS BLOCK': 'BOSS-BLOCK',
  'BOSS ATTACK': 'BOSS-ANGRIFF',
  'BOSS DRIFT': 'BOSS-DRIFT',
  'BOSS SHORTCUT': 'BOSS-ABKÜRZUNG',
  'CHAIN LOST': 'KETTE VERLOREN',

  // ── vehicle-signatures.ts ─────────────────────────────────────────────
  'FLOW STATE': 'IM FLOW',
  'A longer style-chain window rewards smooth, connected driving.':
    'Ein längeres Fenster für Style-Ketten belohnt flüssiges, zusammenhängendes Fahren.',
  'LONG BURN': 'LANGER ATEM',
  'High-speed aero stretches every tank of nitro further.':
    'Highspeed-Aero holt aus jeder Nitro-Füllung mehr heraus.',
  'APEX HUNTER': 'APEX-JÄGER',
  'Perfect corners bank more style and exit nitro.':
    'Perfekte Kurven bringen mehr Style und Nitro am Ausgang.',
  'PRECISION CHAIN': 'PRÄZISIONSKETTE',
  'Clean corners and drafting build a stronger technical chain.':
    'Saubere Kurven und Windschatten bauen eine stärkere technische Kette auf.',
  'SMOKE SHOW': 'QUALMSHOW',
  'Caught drifts and sustained slides pay a larger style dividend.':
    'Abgefangene Drifts und lange Slides zahlen sich mit mehr Style aus.',
  'TRAILBLAZER': 'WEGBEREITER',
  'Rough and technical alternate routes pay extra.': 'Raue und technische Alternativrouten bringen einen Bonus.',
  'ROAD PRESENCE': 'STRASSENPRÄSENZ',
  'Drafting, clean passes and takedowns reward the heavyweight line.':
    'Windschatten, saubere Überholmanöver und Takedowns belohnen die Schwergewichtslinie.',
  'OVERMATCH': 'ÜBERMACHT',
  'Takedowns and cannon plays build extra style and nitro.':
    'Takedowns und Kanonentreffer bringen extra Style und Nitro.',
  'AIR ATTACK': 'LUFTANGRIFF',
  'Aerial control turns clean landings into larger rewards.':
    'Kontrolle in der Luft macht saubere Landungen lohnender.',
  'WILD LINE': 'WILDE LINIE',
  'Jumps and gravel shortcuts refill the attack faster.':
    'Sprünge und Schotter-Abkürzungen laden den Angriff schneller auf.',
  'RELISH THE SLIDE': 'SLIDE MIT SENF',
  'Long, saucy drifts pay a larger style dividend.': 'Lange, würzige Drifts zahlen sich mit mehr Style aus.',
  'DUCK AND WEAVE': 'ENTEN-SLALOM',
  'COUCH POTATO FLOW': 'COUCHPOTATO-FLOW',
  'A longer style-chain window rewards relaxed, connected driving.':
    'Ein längeres Fenster für Style-Ketten belohnt entspanntes, zusammenhängendes Fahren.',
  'BIG AIR BUBBLE': 'BIG-AIR-BLASE',
  'Jumps and rough shortcuts refill the attack faster.':
    'Sprünge und raue Abkürzungen laden den Angriff schneller auf.',

  // ── Shared enums, shown upper-cased (t(value.toUpperCase())) ──────────
  // TimeOfDay / Weather
  'DAWN': 'DÄMMERUNG',
  'DAY': 'TAG',
  'SUNSET': 'ABENDROT',
  'NIGHT': 'NACHT',
  'CLEAR': 'KLAR',
  'HAZE': 'DUNST',
  'RAIN': 'REGEN',
  'SNOW': 'SCHNEE',
  // RivalStyle ('GRIP', 'DRIFT', 'SPRINTER' stay) + fallback
  'BULLY': 'RAUBEIN',
  'TRICKSTER': 'TRICKSER',
  'AI': 'KI',
  // NpcDifficulty ('SPORT' stays)
  'ROOKIE': 'NEULING',
  'PRO': 'PROFI',
  // Alternate route kind / risk / surface ('ASPHALT' stays; 'SHORTCUT' above)
  'LOOP': 'SCHLEIFE',
  'SAFE': 'SICHER',
  'TECHNICAL': 'TECHNISCH',
  'EXTREME': 'EXTREM',
  'GRAVEL': 'SCHOTTER',
  'SERVICE': 'SERVICEWEG',
  // DynamicTrackEventKind via kind.replace('-', ' ').toUpperCase()
  'LANE CLOSURE': 'SPURSPERRUNG',
  'STREET CROSSING': 'QUERVERKEHR',
  'ROADBLOCK': 'STRASSENSPERRE',

  // ── Room / server errors (RoomError, InputError, HTTP `error`) ────────
  'This race has already started': 'Dieses Rennen läuft bereits',
  'This room is full': 'Dieser Raum ist voll',
  'Unknown player': 'Unbekannter Fahrer',
  'Only the host can change the race': 'Nur der Host kann das Rennen ändern',
  'Cars are locked once the grid is released': 'Sobald das Feld freigegeben ist, sind die Autos gesperrt',
  'Unknown car': 'Unbekanntes Auto',
  'Only the host can remove drivers': 'Nur der Host kann Fahrer entfernen',
  'Use Leave to exit your own lobby': 'Nutze „Verlassen“, um deine eigene Lobby zu verlassen',
  'That driver is no longer in the room': 'Dieser Fahrer ist nicht mehr im Raum',
  'Only the host can start the race': 'Nur der Host kann das Rennen starten',
  'Only the host can restart the race': 'Nur der Host kann das Rennen neu starten',
  'The race can only be changed in the lobby': 'Das Rennen kann nur in der Lobby geändert werden',
  'Unknown race map': 'Unbekannte Strecke',
  'Every party member must reconnect and be ready before launch':
    'Alle in der Gruppe müssen verbunden und bereit sein, bevor es losgeht',
  'Road Rage needs at least one marked rival': 'Road Rage braucht mindestens einen markierten Rivalen',
  'Elimination needs at least two racers': 'Eliminierung braucht mindestens zwei Fahrer',
  'Quick restart is available only in NPC-only races': 'Schnellneustart gibt es nur in Rennen gegen KI-Gegner',
  'The server has reached room capacity': 'Der Server hat keine freien Räume mehr',
  'The Daily setup is stale or does not match today’s published challenge':
    'Das Daily-Setup ist veraltet oder passt nicht zur heutigen Challenge',
  'This invite link is invalid': 'Dieser Einladungslink ist ungültig',
  'The host has banned you from this lobby': 'Der Host hat dich aus dieser Lobby verbannt',
  'This lobby is private': 'Diese Lobby ist privat',
  'Only the current host can move the party': 'Nur der aktuelle Host kann die Gruppe weiterziehen',
  'The party has not selected that event': 'Die Gruppe hat dieses Event nicht gewählt',
  'No successor map is available': 'Keine nächste Strecke verfügbar',
  'The party host is no longer available': 'Der Host der Gruppe ist nicht mehr da',
  'Race room not found': 'Rennraum nicht gefunden',
  'Invalid player token': 'Ungültiger Spieler-Token',
  'Player is not part of the successor race': 'Du bist nicht Teil des nächsten Rennens',
  'This saved race is waiting for server capacity': 'Dieses gespeicherte Rennen wartet auf freie Serverkapazität',
  'This room has reached its spectator limit': 'Dieser Raum hat das Zuschauerlimit erreicht',
  'Arcade Lab presets are available in offline mode only': 'Arcade-Lab-Presets gibt es nur im Offline-Modus',
  'Daily challenge is unavailable': 'Die Daily Challenge ist nicht verfügbar',
  'Too many requests. Try again shortly.': 'Zu viele Anfragen. Versuch es gleich noch mal.',
  'Request body too large': 'Anfrage zu groß',
  'Malformed request body': 'Fehlerhafte Anfrage',
  'Internal server error': 'Interner Serverfehler',
  'Invalid message': 'Ungültige Nachricht',
  'Message too large': 'Nachricht zu groß',
  'Authentication payload too large': 'Anmeldedaten zu groß',
  'dailyChallengeId must be a UTC date in YYYY-MM-DD format': 'Ungültige Daily-Challenge-ID',
  'dailyChallengeId must be a valid UTC date': 'Ungültige Daily-Challenge-ID',
  'npcRoster must list at most 11 rival difficulties': 'Maximal 11 KI-Gegner erlaubt',

  // lobby.ts parseLobbyPatch (sent back as ws `error` message)
  'Lobby settings missing': 'Lobby-Einstellungen fehlen',
  'Invalid map': 'Ungültige Strecke',
  'Invalid event format': 'Ungültiges Eventformat',
  'Invalid lap count': 'Ungültige Rundenzahl',
  'Invalid time of day': 'Ungültige Tageszeit',
  'Invalid weather': 'Ungültiges Wetter',
  'Invalid rival grid': 'Ungültiges Gegnerfeld',
  'Invalid visibility': 'Ungültige Sichtbarkeit',
  'Invalid player limit': 'Ungültiges Spielerlimit',
  'Invalid rule set': 'Ungültiges Regelwerk',
  'Empty lobby edit': 'Keine Lobby-Änderung',

  // client-message-schema.ts (ws `error` / close 4003 reason)
  'Unsupported message': 'Nicht unterstützte Nachricht',
  'Input payload missing': 'Eingabedaten fehlen',
  'Invalid input race attempt': 'Ungültiger Rennversuch',
  'Resume payload missing': 'Fortsetzungsdaten fehlen',
  'Ready state missing': 'Bereit-Status fehlt',
  'Invalid restart race attempt': 'Ungültiger Neustartversuch',
  'Invalid post-race vote': 'Ungültige Abstimmung',
  'Ping timestamp missing': 'Ping-Zeitstempel fehlt',
  'Invalid kick': 'Ungültiger Rauswurf',
  'Authentication required': 'Anmeldung erforderlich',
  'Invalid room id': 'Ungültige Raum-ID',
  'Invalid invite token': 'Ungültiger Einladungscode',
  'Invalid player credentials': 'Ungültige Spielerdaten',

  // WebSocket close reasons (onTerminalClose → renderFatal / startup notice)
  'Session expired': 'Sitzung abgelaufen',
  'This race hit a server fault and was stopped.': 'Dieses Rennen wurde wegen eines Serverfehlers abgebrochen.',
  'Authentication timed out': 'Zeitüberschreitung bei der Anmeldung',
  'You were banned from this lobby': 'Du wurdest aus dieser Lobby verbannt',
  'The host removed you from this lobby': 'Der Host hat dich aus dieser Lobby entfernt',
  'Replaced by a newer connection': 'Durch eine neuere Verbindung ersetzt',
  'Party moved to the next grid': 'Die Gruppe ist zum nächsten Rennen weitergezogen',
  'Rate limit exceeded': 'Zu viele Nachrichten',
  'Server shutting down': 'Server wird heruntergefahren',
  'Unable to save session': 'Sitzung konnte nicht gespeichert werden',
  'Unable to save successor race': 'Nächstes Rennen konnte nicht gespeichert werden',
  'Unable to retire previous race': 'Vorheriges Rennen konnte nicht beendet werden',

  // ── maps/*.json: taglines ─────────────────────────────────────────────
  'Wet switchbacks above a sleeping city.': 'Nasse Serpentinen über einer schlafenden Stadt.',
  'Fast cliff roads in the last light of day.': 'Schnelle Klippenstraßen im letzten Tageslicht.',
  'Cross the Spree, thread the monuments and open it up on the capital boulevards.':
    'Über die Spree, vorbei an den Monumenten – und auf den Boulevards der Hauptstadt voll aufs Gas.',
  'Sandstone arcades, Aare bridges and a fast climb through the old town.':
    'Sandstein-Lauben, Aarebrücken und ein schneller Anstieg durch die Altstadt.',
  'Ancient ruins, river jumps and a canopy that swallows the sun.':
    'Uralte Ruinen, Sprünge über den Fluss und ein Blätterdach, das die Sonne verschluckt.',
  'Wide city avenues, green parkland and a flowing lap around the Maschsee.':
    'Breite Stadtalleen, grüne Parks und eine flüssige Runde um den Maschsee.',
  'A golden-hour run from the Reuss to the lake beneath the Alpine skyline.':
    'Ein Lauf im goldenen Abendlicht von der Reuss zum See vor der Alpenkulisse.',
  'Glacial water, steel bridges and two ways through the high valley.':
    'Gletscherwasser, Stahlbrücken und zwei Wege durchs Hochtal.',
  'Technical midnight blocks beneath electric towers.': 'Technische Mitternachtsblocks unter leuchtenden Türmen.',
  'Dust, heat and blind canyon compressions.': 'Staub, Hitze und blinde Kompressionen im Canyon.',
  'Snow-packed night stages between frozen pines and timber mills.':
    'Verschneite Nachtetappen zwischen gefrorenen Kiefern und Sägewerken.',
  'Mountain switchbacks descend to an oasis bridge and a hidden canyon line.':
    'Bergserpentinen hinab zu einer Oasenbrücke und einer versteckten Canyonlinie.',
  'Low gravity, crater jumps and Earth hanging in a black sky.':
    'Geringe Schwerkraft, Kratersprünge und die Erde am schwarzen Himmel.',

  // maps/*.json: location (others are place names and stay)
  'Black Forest, DE': 'Schwarzwald, DE',
  'Río Jade, Petén Basin': 'Río Jade, Petén-Becken',
  'Mare Tranquillitatis, The Moon': 'Mare Tranquillitatis, Mond',

  // maps/*.json: alternate route names (race-director upper-cases them)
  'Avalanche Gallery': 'Lawinengalerie',
  'Riviera Panorama': 'Riviera-Panorama',
  'Tiergarten Midnight Cut': 'Tiergarten-Nachtschneise',
  'Lauben Arcade Cut': 'Lauben-Abkürzung',
  'Herrenhausen Park Axis': 'Herrenhäuser Parkachse',
  'Musegg Service Lane': 'Musegg-Serviceweg',
  'Waters Edge Cut': 'Uferschneise',
  'Eagle Mountain Loop': 'Adlerberg-Schleife',
  'Midnight Service Cut': 'Mitternachts-Serviceweg',
  'Quarry Cut': 'Steinbruch-Abkürzung',
  'Timber Line Cut': 'Baumgrenzen-Abkürzung',
  'Dry River Cut': 'Trockenfluss-Abkürzung',
  'Condor Mesa Loop': 'Condor-Mesa-Schleife',

  // maps/*.json: road section names (descriptive ones; currently not shown in UI)
  'Valley Launch': 'Talstart',
  'Icefall Switchbacks': 'Eisfall-Serpentinen',
  'Summit Gallery': 'Gipfelgalerie',
  'City Descent': 'Stadtabfahrt',
  'Harbour Launch': 'Hafenstart',
  'Lume Viaduct': 'Lume-Viadukt',
  'Riviera Village': 'Riviera-Dorf',
  'Cliffside Run': 'Klippenpassage',
  'Tempelhof Launch': 'Start Tempelhof',
  'Spree Crossing': 'Spreeüberquerung',
  'East Side Sprint': 'East-Side-Sprint',
  'Bundesplatz Launch': 'Start Bundesplatz',
  'Nydegg Crossing': 'Nydegg-Querung',
  'Altstadt Arcades': 'Altstadt-Lauben',
  'Aare Bank Sprint': 'Aareufer-Sprint',
  'Canopy Launch': 'Blätterdach-Start',
  'Temple Steps': 'Tempelstufen',
  'Vine Ridge': 'Lianengrat',
  'River Run': 'Flusslauf',
  'City Ring Launch': 'Start Cityring',
  'Maschsee Sweepers': 'Maschsee-Bögen',
  'Herrenhausen Axis': 'Herrenhäuser Achse',
  'Messe Sprint': 'Messe-Sprint',
  'Bahnhofquai Launch': 'Start Bahnhofquai',
  'Reuss Crossing': 'Reussquerung',
  'Musegg Old Town': 'Altstadt Musegg',
  'Lake Promenade': 'Seepromenade',
  'Lakeside Launch': 'Seeufer-Start',
  'Inlet Bridge': 'Zuflussbrücke',
  'Eagle High Road': 'Adler-Höhenstraße',
  'Glacier Descent': 'Gletscherabfahrt',
  'Market Chicane': 'Markt-Schikane',
  'Dockyard Circuit': 'Werftrunde',
  'Midnight Express': 'Mitternachtsexpress',
  'Quarry Launch': 'Steinbruch-Start',
  'Compressor Switchbacks': 'Kompressor-Serpentinen',
  'Mesa Ridge': 'Mesa-Grat',
  'Redline Descent': 'Redline-Abfahrt',
  'Village Launch': 'Dorfstart',
  'Timber Climb': 'Holzfäller-Anstieg',
  'Mill Ridge': 'Mühlengrat',
  'Valley Sprint': 'Talsprint',
  'Riverbed Launch': 'Flussbett-Start',
  'Sunspire Switchbacks': 'Sunspire-Serpentinen',
  'Oasis Sprint': 'Oasensprint',
  'Landing Zone': 'Landezone',
  'Crater Hop': 'Kratersprung',
  'Highland Arc': 'Hochlandbogen',
  'Mare Straight': 'Mare-Gerade',

  // maps/*.json: landmark names (descriptive ones; currently not shown in UI)
  'Val Marea Ski Station': 'Skistation Val Marea',
  'Lume Lighthouse': 'Leuchtturm Lume',
  'Kapellbruecke & Wasserturm': 'Kapellbrücke & Wasserturm',
  'Mirror Cable Terminal': 'Seilbahnstation Mirror',
  'Aurelio Quarry': 'Steinbruch Aurelio',
  'Schwarzwald Ski Hut': 'Schwarzwälder Skihütte',
  'Condor Observatory': 'Condor-Observatorium',
  'Jade Pyramid': 'Jadepyramide',
  'Tranquility Habitat': 'Tranquility-Habitat',
};
